import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Terminal, Cpu, CheckCircle } from 'lucide-react';

const steps = [
    {
        id: 1,
        title: "Telegram Trigger",
        description: "User sends /predict command",
        icon: Send,
        color: "text-primary"
    },
    {
        id: 2,
        title: "AI Analysis",
        description: "Lobster engine scans global sentiment",
        icon: Terminal,
        color: "text-secondary"
    },
    {
        id: 3,
        title: "Lobster Execution",
        description: "OpenClaw agents execute trade",
        icon: Cpu,
        color: "text-primary"
    },
    {
        id: 4,
        title: "Results",
        description: "Outcome sent back to chat",
        icon: CheckCircle,
        color: "text-secondary"
    }
];

export const TelegramSection = () => {
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % steps.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
        <section id="how-it-works" className="py-24 bg-[#1a1a1b] border-t border-white/5 relative overflow-hidden">
            <div className="container mx-auto px-4">
                <div className="mb-16 border-b border-white/5 pb-10 flex items-center gap-4">
                    <span className="text-3xl">🎯</span>
                    <div>
                        <h2 className="text-2xl font-bold text-white uppercase tracking-tight">How It Works</h2>
                        <p className="text-[#6a6a6a] text-sm">Seamless integration from Telegram to on-chain execution.</p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Visualizer */}
                    <div className="relative h-[450px] border border-white/5 rounded bg-[#242425] overflow-hidden flex items-center justify-center p-8">
                        <AnimatePresence mode="wait">
                            {activeStep === 0 && (
                                <motion.div
                                    key="step1"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="w-full max-w-sm bg-[#1E2329] rounded-xl p-4 shadow-xl border border-white/5"
                                >
                                    <div className="flex items-center gap-3 mb-4 border-b border-white/10 pb-2">
                                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">T</div>
                                        <div className="text-white font-bold">Telegram Chat</div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="bg-[#2B5278] self-end rounded-lg rounded-tr-none p-3 text-sm text-white max-w-[80%] ml-auto">
                                            /predict BTC 100k
                                        </div>
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 1 }}
                                            className="bg-[#182533] self-start rounded-lg rounded-tl-none p-3 text-sm text-white max-w-[80%]"
                                        >
                                            <span className="animate-pulse">Analyzing market request...</span>
                                        </motion.div>
                                    </div>
                                </motion.div>
                            )}

                            {activeStep === 1 && (
                                <motion.div
                                    key="step2"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className="w-full max-w-md font-mono text-xs"
                                >
                                    <div className="bg-black border border-secondary/30 rounded-lg p-4 shadow-[0_0_30px_rgba(0,212,170,0.1)]">
                                        <div className="text-secondary mb-2"> &gt;_ System Analysis Initiated</div>
                                        <div className="text-secondary/80"> [DATA] Fetching volume across 12 exchanges...</div>
                                        <div className="text-secondary/80"> [SENTIMENT] Twitter: 68% Bullish</div>
                                        <div className="text-secondary/80"> [RISK] Volatility Index: MODERATE</div>
                                        <div className="mt-2 text-white animate-pulse"> Calculating win probability...</div>
                                    </div>
                                </motion.div>
                            )}

                            {activeStep === 2 && (
                                <motion.div
                                    key="step3"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="relative w-64 h-64"
                                >
                                    <div className="absolute inset-0 border-2 border-dashed border-red-500/30 rounded-full animate-[spin_10s_linear_infinite]" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-center">
                                            <Cpu className="w-16 h-16 text-red-500 mx-auto mb-2" />
                                            <div className="text-secondary font-bold uppercase tracking-widest">Executing</div>
                                            <div className="text-xs text-secondary/70">Lobster Consensus Reached</div>
                                        </div>
                                    </div>
                                    {[0, 1, 2, 3, 4, 5].map(i => (
                                        <div key={i} className="absolute top-1/2 left-1/2 w-3 h-3 bg-red-500 rounded-full"
                                            style={{ transform: `rotate(${i * 60}deg) translate(120px) rotate(-${i * 60}deg)` }} />
                                    ))}
                                </motion.div>
                            )}

                            {activeStep === 3 && (
                                <motion.div
                                    key="step4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="w-full max-w-sm bg-[#1E2329] rounded-xl p-4 shadow-xl border border-white/5"
                                >
                                    <div className="flex items-center gap-3 mb-4 border-b border-white/10 pb-2">
                                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">T</div>
                                        <div className="text-white font-bold">Telegram Chat</div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="bg-[#182533] rounded-lg rounded-tl-none p-3 text-sm text-white">
                                            <div className="flex items-center gap-2 text-secondary font-bold mb-1">
                                                <CheckCircle className="w-4 h-4" /> Trade Executed
                                            </div>
                                            <p className="text-gray-300">Position opened on Polymarket. Win probability: 87%</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Timeline */}
                    <div className="space-y-6">
                        {steps.map((step, index) => (
                            <div
                                key={step.id}
                                className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-500 ${activeStep === index ? 'bg-white/5 border border-white/10' : 'opacity-50'}`}
                            >
                                <div className={`mt-1 p-2 rounded-lg bg-black/50 ${step.color}`}>
                                    <step.icon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className={`font-bold text-lg ${activeStep === index ? 'text-white' : 'text-muted-foreground'}`}>{step.title}</h3>
                                    <p className="text-sm text-muted-foreground">{step.description}</p>
                                </div>
                                {activeStep === index && (
                                    <div className="ml-auto self-center">
                                        <span className="relative flex h-3 w-3">
                                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${step.color.replace('text', 'bg')}`}></span>
                                            <span className={`relative inline-flex rounded-full h-3 w-3 ${step.color.replace('text', 'bg')}`}></span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
